import React, { useEffect, useMemo } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { actionAtom } from 'atoms/atoms'
import { useAtom } from 'jotai'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Avatar, toast } from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { useFormFields } from 'hooks/forms/useFormFields'
import { usePostAddMerchantMutation } from 'hooks/mutations/usePostAddMerchantMutation'
import { usePutEditMerchantMutation } from 'hooks/mutations/usePutEditMerchantMutation'
import { useWalletsQuery } from 'hooks/queries/useWalletsQuery'
import { useActiveWallets } from 'hooks/useActiveWallets'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse } from 'types/beneficiaries'
import { ActionIds, AddMerchantPayload, EditMerchantPayload } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import {
  MerchantFormData,
  MerchantFormKeys,
  merchantFormSchema,
} from '../schemas/merchantFormSchema'

interface UseMerchantFormProps {
  action: 'EDIT' | 'CREATE'
}

export const useMerchantForm = ({ action }: UseMerchantFormProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [sheetAction, setSheetAction] = useAtom(actionAtom)
  const merchant = sheetAction?.meta?.merchant

  const { mutate: mutateAddMerchant, isPending: isLoadingAddMerchant } =
    usePostAddMerchantMutation()
  const { mutate: mutateEditMerchant, isPending: isLoadingEditMerchant } =
    usePutEditMerchantMutation()

  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const { data: wallets = [], isPending: isLoadingWallets } = useWalletsQuery()

  const formSchema = merchantFormSchema(t)

  const form = useForm<MerchantFormData>({
    defaultValues: {
      merchantName: merchant?.displayName || '',
      walletId: merchant?.wallet?.id ? `${merchant?.wallet?.id}` : '',
      webhookUrl: merchant?.webhookUrl || '',
    },
    resolver: yupResolver(formSchema),
    mode: 'onChange',
  })

  const {
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors, isDirty, isValid },
  } = form

  const formValues = useWatch({ control })

  const { setInputField, setComboboxField } = useFormFields<MerchantFormKeys>(
    formSchema,
    form,
    formValues
  )

  const activeWallets = useActiveWallets(wallets)

  const getWalletOptions = useMemo(() => {
    if (!activeWallets?.length) {
      return []
    }
    return activeWallets.map(wallet => {
      return {
        value: `${wallet?.id}`,
        leftText: wallet?.description,
        rightText: `${wallet?.balance} ${wallet?.currency?.code}`,
        LeftElement: <Avatar name={wallet?.currency?.code?.toLowerCase()} size="small" />,
      }
    })
  }, [activeWallets])

  const formFields: { [key in MerchantFormKeys] } = {
    merchantName: setInputField({ key: 'merchantName' }),
    walletId: setComboboxField({
      key: 'walletId',
      items: getWalletOptions,
      defaultValue: watch('walletId'),
    }),
    webhookUrl: setInputField({ key: 'webhookUrl' }),
  }

  useEffect(() => {
    if (action === 'CREATE') {
      track.Amp.track(AmplitudeEvent.MERCHANT_INITIATE, {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.VIEW,
      })
    }
    if (action === 'EDIT') {
      track.Amp.track(AmplitudeEvent.MERCHANT_EDIT, {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.VIEW,
      })
    }
  }, [action])

  const shouldDisable = () => {
    const hasErrors = Object.keys(errors).length > 0

    if (action === 'CREATE') {
      return hasErrors || !isDirty || !isValid
    }

    return hasErrors || (!isDirty && !!merchant?.wallet)
  }

  const onSubmit: SubmitHandler<MerchantFormData> = async values => {
    if (shouldDisable()) {
      return
    }
    resetRequestError()

    if (action === 'CREATE') {
      const merchantDetails: AddMerchantPayload = {
        displayName: values?.merchantName,
        ...(!!values?.webhookUrl && { webhookUrl: values?.webhookUrl }),
        wallet: {
          id: Number(values?.walletId),
        },
      }

      mutateAddMerchant(merchantDetails, {
        onSuccess: data => {
          queryClient.invalidateQueries({ queryKey: reactQueryKeys.merchants() })
          track.Amp.track(AmplitudeEvent.EOF_INITIATE_MERCHANT_SUCCESS, {
            category: AmplitudeEventCategory.APPLICATION,
            action: AmplitudeEventAction.CLICK,
          })
          setSheetAction({
            ...sheetAction,
            actionId: ActionIds.MERCHANT_DETAILS,
            header: null,
            meta: {
              ...sheetAction?.meta,
              merchant: data,
            },
          })
        },
        onError: (error: BaseErrorResponse) => {
          setRequestError({
            handledMessage: t('oopsSomethingWentWrongContracted'),
            show: true,
          })
          logSentryError('Error: usePostAddMerchantMutation', error)
        },
      })
    } else {
      const merchantDetails: EditMerchantPayload = {
        displayName: values?.merchantName,
        ...(!!values?.webhookUrl && { webhookUrl: values?.webhookUrl }),
        wallet: Number(values?.walletId),
        merchant,
      }

      mutateEditMerchant(merchantDetails, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: reactQueryKeys.merchants() })
          track.Amp.track(AmplitudeEvent.EOF_EDIT_MERCHANT_SUCCESS, {
            category: AmplitudeEventCategory.APPLICATION,
            action: AmplitudeEventAction.CLICK,
          })
          toast({ header: t('merchantUpdated'), variant: 'success' })
          sheetAction?.onClose?.()
        },
        onError: (error: BaseErrorResponse) => {
          setRequestError({
            handledMessage: t('oopsSomethingWentWrongContracted'),
            show: true,
          })
          logSentryError('Error: usePutEditMerchantMutation', error)
        },
      })
    }
  }

  return {
    formFields,
    formValues,
    isLoadingWallets,
    errors,
    control,
    isLoadingAddMerchant,
    isLoadingEditMerchant,
    requestError,
    handleSubmit,
    onSubmit,
    setValue,
    shouldDisable,
    getWalletOptions,
  }
}
